import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { setAuthToken } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Users, UserPlus, Shield, ShieldOff, KeyRound, BarChart3, Trash2, Eye,
  DollarSign, TrendingDown, Wallet, PiggyBank, LineChart as LineChartIcon,
  Download, Upload, Database, AlertTriangle, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Unlock,
  Filter, X, Search, History, ShieldAlert, ArrowUp, ArrowDown
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const fmt = (n) => fmtMoney(n);
const COLORS = ['hsl(136,18%,35%)', 'hsl(43,74%,50%)', 'hsl(8,52%,51%)', 'hsl(197,37%,44%)', 'hsl(280,45%,55%)', 'hsl(30,70%,50%)'];

function UserDashboardPanel({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availMonths, setAvailMonths] = useState([]);
  const [period, setPeriod] = useState(''); // 'YYYY-MM' or '' = latest

  // Load list of months once per user
  useEffect(() => {
    if (!userId) return;
    setPeriod('');
    api.get(`/api/admin/users/${userId}/available-months`)
      .then(({ data }) => setAvailMonths(data || []))
      .catch(() => setAvailMonths([]));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const params = {};
    if (period) { const [y, m] = period.split('-'); params.year = y; params.month = m; }
    api.get(`/api/admin/users/${userId}/dashboard`, { params })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, period]);

  const navPeriod = (delta) => {
    const base = period || (data?.period && data.period !== 'all' ? data.period : '');
    if (!base) return;
    const [y, m] = base.split('-');
    let mi = parseInt(m, 10) + delta, yi = parseInt(y, 10);
    while (mi > 12) { mi -= 12; yi++; }
    while (mi < 1) { mi += 12; yi--; }
    setPeriod(`${yi}-${String(mi).padStart(2, '0')}`);
  };
  const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const labelOf = (ym) => { const [y, m] = ym.split('-'); return `${MN[parseInt(m, 10) - 1]} ${y}`; };

  if (loading && !data) return <div className="space-y-3 animate-pulse">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-muted rounded" />)}</div>;
  if (!data) return <p className="text-sm text-muted-foreground py-4 text-center">No data available</p>;

  const catData = Object.entries(data.category_breakdown || {}).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, value]) => ({ name, value: Math.round(value) }));
  const currentValue = period || data.period;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-user-month-controls">
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => navPeriod(-1)} data-testid="admin-user-prev-month"><ChevronLeft className="h-3.5 w-3.5" /></Button>
        <Select value={currentValue !== 'all' ? currentValue : ''} onValueChange={(v) => setPeriod(v)}>
          <SelectTrigger className="h-7 text-xs w-[150px]" data-testid="admin-user-month-select">
            <CalendarIcon className="h-3.5 w-3.5 mr-1" />
            <SelectValue placeholder="Latest month" />
          </SelectTrigger>
          <SelectContent>
            {availMonths.map((mn) => <SelectItem key={mn.month} value={mn.month}>{labelOf(mn.month)} ({mn.count})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => navPeriod(1)} data-testid="admin-user-next-month"><ChevronRight className="h-3.5 w-3.5" /></Button>
        <Badge variant="outline" className="text-xs ml-1">{data.period === 'all' ? 'All data' : labelOf(data.period)}</Badge>
        <span className="text-xs text-muted-foreground">{data.transaction_count} txns &middot; {data.statement_count} statements</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: DollarSign, label: 'Income', value: fmt(data.income), color: '' },
          { icon: TrendingDown, label: 'Expenses', value: fmt(data.net_expenses), color: '' },
          { icon: PiggyBank, label: 'Savings', value: fmt(data.savings), color: data.savings >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500' },
          { icon: Wallet, label: 'Net Worth', value: fmt(data.net_worth), color: data.net_worth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="p-3 rounded-md border border-border">
            <div className="flex items-center gap-1.5 mb-1"><Icon className="h-3.5 w-3.5 text-muted-foreground" /><span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{label}</span></div>
            <p className={`text-lg font-semibold tabular-nums font-['Outfit'] ${color}`}>{value}</p>
          </div>
        ))}
      </div>
      {data.refunds > 0 && <div className="p-2 rounded-md bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-400">Refunds this period: {fmt(data.refunds)}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-md border border-border">
          <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Investments</span>
          <p className="text-lg font-semibold tabular-nums font-['Outfit'] text-green-600 dark:text-green-400">{fmt(data.investments)}</p>
        </div>
        <div className="p-3 rounded-md border border-border">
          <span className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground">Total Assets / Liabilities</span>
          <p className="text-sm tabular-nums mt-0.5"><span className="text-green-600 dark:text-green-400">{fmt(data.total_assets)}</span> / <span className="text-red-500">{fmt(data.total_liabilities)}</span></p>
        </div>
      </div>
      {catData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-center">
          <ResponsiveContainer width="100%" height={150}>
            <PieChart><Pie data={catData} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value" stroke="none">{catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: '11px', borderRadius: '6px' }} /></PieChart>
          </ResponsiveContainer>
          <div className="space-y-1">{catData.map((d, i) => <div key={d.name} className="flex justify-between text-xs"><div className="flex items-center gap-1.5"><div className="h-2 w-2 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span className="text-muted-foreground truncate">{d.name}</span></div><span className="tabular-nums font-medium">{fmt(d.value)}</span></div>)}</div>
        </div>
      )}
    </div>
  );
}

function BackupRestorePanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const [report, setReport] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get('/api/admin/backup/stats'); setStats(data); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/api/admin/backup/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url; a.download = `moneymap-backup-${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setBusy(false); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Choose a backup file first'); return; }
    const warn = importMode === 'replace'
      ? 'REPLACE mode will WIPE all existing data and replace it with the backup. Continue?'
      : 'MERGE mode will add records from the backup, skipping any that already exist. Continue?';
    if (!window.confirm(warn)) return;
    setBusy(true); setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('mode', importMode);
      const { data } = await api.post('/api/admin/backup/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReport(data);
      toast.success('Import complete');
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const totalDocs = stats?.total_documents ?? 0;

  return (
    <Card className="border border-border shadow-none" data-testid="backup-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" /> Backup &amp; Restore
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Current DB:</span>
          {stats ? (
            <>
              <Badge variant="outline" className="font-mono">{totalDocs} docs</Badge>
              {Object.entries(stats.collections).filter(([, v]) => v > 0).map(([k, v]) => (
                <Badge key={k} variant="secondary" className="text-[10px]">{k}: {v}</Badge>
              ))}
            </>
          ) : <span>loading...</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export */}
          <div className="space-y-2 p-3 rounded-md border border-border">
            <div className="flex items-center gap-2"><Download className="h-4 w-4 text-primary" /><h4 className="text-sm font-medium">Export full backup</h4></div>
            <p className="text-xs text-muted-foreground">Downloads a JSON file with all users, transactions, statements, income, assets, liabilities, categories and snapshots. Uploaded source files (PDFs/CSVs) are not included.</p>
            <Button size="sm" onClick={handleExport} disabled={busy} data-testid="backup-export-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" />{busy ? 'Working...' : 'Download backup'}
            </Button>
          </div>

          {/* Import */}
          <div className="space-y-2 p-3 rounded-md border border-border">
            <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-primary" /><h4 className="text-sm font-medium">Restore from backup</h4></div>
            <div className="space-y-2">
              <Input
                type="file" accept="application/json,.json"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="backup-file-input"
                className="text-xs"
              />
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger className="h-8 text-xs" data-testid="backup-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge — add new, keep existing</SelectItem>
                  <SelectItem value="replace">Replace — wipe &amp; restore</SelectItem>
                </SelectContent>
              </Select>
              {importMode === 'replace' && (
                <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>Destructive — all current data will be erased.</span>
                </div>
              )}
              <Button size="sm" variant={importMode === 'replace' ? 'destructive' : 'default'} onClick={handleImport} disabled={busy || !importFile} data-testid="backup-import-btn">
                <Upload className="h-3.5 w-3.5 mr-1.5" />{busy ? 'Importing...' : 'Restore now'}
              </Button>
            </div>
          </div>
        </div>

        {report && (
          <div className="p-3 rounded-md bg-muted/40 border border-border text-xs space-y-1" data-testid="backup-report">
            <p className="font-medium">Import report ({report.mode})</p>
            {Object.entries(report.collections).map(([k, v]) => (
              <div key={k} className="flex justify-between font-mono">
                <span className="text-muted-foreground">{k}</span>
                <span>{Object.entries(v).map(([kk, vv]) => `${kk}:${vv}`).join('  ')}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ============================================================================
// FAILED LOGIN ATTEMPTS PANEL
// ============================================================================
function FailedLoginAttemptsPanel() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAttempts = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/admin/failed-logins');
      setAttempts(data);
    } catch (e) {
      toast.error('Failed to load login attempts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttempts();
  }, []);

  const deleteAttempt = async (identifier) => {
    if (!window.confirm(`Delete failed login record for "${identifier}"?`)) return;
    try {
      await api.delete(`/api/admin/failed-logins/${encodeURIComponent(identifier)}`);
      toast.success('Deleted');
      loadAttempts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const clearAll = async () => {
    if (!window.confirm('Clear ALL failed login attempts? This cannot be undone.')) return;
    try {
      await api.delete('/api/admin/failed-logins');
      toast.success('All failed login attempts cleared');
      loadAttempts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear');
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <Card className="border border-border shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-destructive" /> Failed Login Attempts
          </CardTitle>
          {attempts.length > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={clearAll}
              className="h-7 text-xs"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear All ({attempts.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">Loading...</div>
        ) : attempts.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No failed login attempts</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Username</TableHead>
                  <TableHead className="w-[120px]">IP Address</TableHead>
                  <TableHead className="w-[80px] text-center">Attempts</TableHead>
                  <TableHead className="w-[200px]">Last Attempt</TableHead>
                  <TableHead className="w-[180px]">Status</TableHead>
                  <TableHead className="w-[80px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attempts.map((attempt) => {
                  const isLocked = attempt.locked_until && new Date(attempt.locked_until) > new Date();
                  return (
                    <TableRow key={attempt.identifier}>
                      <TableCell className="font-mono text-xs">
                        {attempt.username || 'Unknown'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {attempt.ip_address || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={attempt.attempts >= 5 ? 'destructive' : 'secondary'}>
                          {attempt.attempts}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatTime(attempt.last_attempt)}
                      </TableCell>
                      <TableCell>
                        {isLocked ? (
                          <Badge variant="destructive" className="text-xs">
                            🔒 Locked until {formatTime(attempt.locked_until)}
                          </Badge>
                        ) : attempt.attempts >= 5 ? (
                          <Badge variant="outline" className="text-xs text-amber-600">
                            ⚠️ Lock expired
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteAttempt(attempt.identifier)}
                          className="h-7 w-7 p-0"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Bulk operations state
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'all', // all, active, inactive
    role: 'all', // all, admin, user
    search: '',
    minTransactions: 0,
    maxTransactions: 1000,
  });

  const load = useCallback(async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([api.get('/api/admin/users'), api.get('/api/admin/stats')]);
      setUsers(usersRes.data); setStats(statsRes.data);
      
      // Update max transactions for filter
      const maxTxns = Math.max(...usersRes.data.map(u => u.transaction_count || 0), 100);
      setFilters(f => ({ ...f, maxTransactions: maxTxns }));
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  
  // Filtered users based on filters (AND logic)
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Status filter
      if (filters.status === 'active' && !u.is_active) return false;
      if (filters.status === 'inactive' && u.is_active) return false;
      
      // Role filter
      if (filters.role !== 'all' && u.role !== filters.role) return false;
      
      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!u.username?.toLowerCase().includes(search) && 
            !u.name?.toLowerCase().includes(search)) return false;
      }
      
      // Transaction count filter
      const txnCount = u.transaction_count || 0;
      if (txnCount < filters.minTransactions || txnCount > filters.maxTransactions) return false;
      
      return true;
    });
  }, [users, filters]);
  
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.role !== 'all') count++;
    if (filters.search) count++;
    if (filters.minTransactions > 0 || filters.maxTransactions < 1000) count++;
    return count;
  }, [filters]);
  
  const clearFilters = () => {
    setFilters({
      status: 'all',
      role: 'all',
      search: '',
      minTransactions: 0,
      maxTransactions: filters.maxTransactions,
    });
  };

  
  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map(u => u._id));
    }
  };
  
  const toggleSelectUser = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  // Bulk operations
  const bulkEnable = async () => {
    if (!window.confirm(`Enable ${selectedUserIds.length} selected user(s)?`)) return;
    try {
      await api.post('/api/admin/users/bulk/enable', { user_ids: selectedUserIds });
      toast.success(`Enabled ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      load();
    } catch { toast.error('Failed'); }
  };
  
  const bulkDisable = async () => {
    if (!window.confirm(`Disable ${selectedUserIds.length} selected user(s)?`)) return;
    try {
      await api.post('/api/admin/users/bulk/disable', { user_ids: selectedUserIds });
      toast.success(`Disabled ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      load();
    } catch { toast.error('Failed'); }
  };
  
  const bulkUnlock = async () => {
    if (!window.confirm(`Clear lockouts for ${selectedUserIds.length} selected user(s)?`)) return;
    try {
      const { data } = await api.post('/api/admin/users/bulk/unlock', { user_ids: selectedUserIds });
      toast.success(`Cleared lockouts for ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      load();
    } catch { toast.error('Failed'); }
  };
  
  const bulkDelete = async () => {
    const msg = `⚠️ PERMANENTLY DELETE ${selectedUserIds.length} user(s) and ALL their data?\n\nThis action CANNOT be undone!\n\nType "DELETE" to confirm:`;
    const confirmation = window.prompt(msg);
    if (confirmation !== 'DELETE') return;
    
    try {
      await api.post('/api/admin/users/bulk/delete', { user_ids: selectedUserIds });
      toast.success(`Deleted ${selectedUserIds.length} user(s)`);
      setSelectedUserIds([]);
      if (selectedUserIds.includes(selectedUser)) setSelectedUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleUser = async (id) => { try { const { data } = await api.put(`/api/admin/users/${id}/toggle`); toast.success(data.message); load(); } catch { toast.error('Failed'); } };
  const resetPassword = async (id, u) => {
    if (!window.confirm(`Reset password for ${u}?`)) return;
    try {
      const { data } = await api.post(`/api/admin/users/${id}/reset-password`);
      const cleared = data.lockouts_cleared || 0;
      toast.success(`Temp: ${data.temporary_password}${cleared ? ` · ${cleared} stale lockout(s) cleared` : ''}`);
    } catch { toast.error('Failed'); }
  };
  const unlockUser = async (id, u) => {
    try {
      const { data } = await api.post(`/api/admin/users/${id}/unlock`);
      toast.success(data.message || `Unlocked ${u}`);
    } catch { toast.error('Failed'); }
  };
  const deleteUser = async (id, u) => { if (!window.confirm(`DELETE "${u}" and ALL data?`)) return; try { await api.delete(`/api/admin/users/${id}`); toast.success('Deleted'); load(); if (selectedUser === id) setSelectedUser(null); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const impersonateUser = async (id, u) => {
    if (!window.confirm(`View as "${u}"?`)) return;
    try {
      const { data } = await api.post(`/api/admin/users/${id}/impersonate`);
      // Cookies are automatically updated by the backend
      await checkAuth(); toast.success(`Now viewing as ${u}`); navigate('/dashboard');
    } catch { toast.error('Failed'); }
  };
  
  const promoteUser = async (id, u) => {
    if (!window.confirm(`Promote "${u}" to Admin? They will have full access to all admin features.`)) return;
    try {
      await api.post(`/api/admin/subscriptions/promote/${id}`);
      toast.success(`${u} promoted to Admin`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote');
    }
  };
  
  const demoteUser = async (id, u) => {
    if (!window.confirm(`Demote "${u}" from Admin to regular user? They will lose admin privileges.`)) return;
    try {
      await api.post(`/api/admin/subscriptions/demote/${id}`);
      toast.success(`${u} demoted to regular user`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to demote');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Admin</h1>
          <p className="text-sm text-muted-foreground mt-1">User management &amp; overview</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild><Button size="sm" data-testid="create-user-btn"><UserPlus className="h-4 w-4 mr-1" />Create User</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle className="font-['Outfit']">Create User</DialogTitle></DialogHeader><CreateUserForm onSave={() => { setShowAdd(false); load(); }} /></DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[{ l: 'Users', v: stats.total_users, i: Users }, { l: 'Active', v: stats.active_users, i: Shield }, { l: 'Transactions', v: stats.total_transactions, i: BarChart3 }, { l: 'Statements', v: stats.total_statements, i: BarChart3 }].map(({ l, v, i: I }) => (
            <Card key={l} className="border border-border shadow-none"><CardContent className="p-4"><div className="flex items-center gap-2 mb-1"><I className="h-4 w-4 text-primary" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">{l}</p></div><p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums">{v}</p></CardContent></Card>
          ))}
        </div>
      )}

      {/* Backup & Restore */}
      <BackupRestorePanel />

      {/* Failed Login Attempts */}
      <FailedLoginAttemptsPanel />

      {/* Users table + inline dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Users list - left panel */}
        <div className="lg:col-span-2">
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Users</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/audit')} className="h-7 text-xs"><History className="h-3 w-3 mr-1" />Audit Log</Button>
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="text-xs">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            
            {/* Filter Panel */}
            <CardContent className="pb-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="h-8 text-xs pl-8"
                  />
                </div>
                <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active Only</SelectItem>
                    <SelectItem value="inactive">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={filters.role} onValueChange={(v) => setFilters(f => ({ ...f, role: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin Only</SelectItem>
                    <SelectItem value="user">User Only</SelectItem>
                  </SelectContent>
                </Select>
                {activeFilterCount > 0 && (
                  <Button variant="outline" size="sm" onClick={clearFilters} className="h-8 text-xs">
                    <X className="h-3 w-3 mr-1" />Clear
                  </Button>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Showing {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
              </div>
            </CardContent>
            
            {/* Bulk Actions Toolbar */}
            {selectedUserIds.length > 0 && (
              <CardContent className="py-2 bg-accent/30">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">{selectedUserIds.length} selected</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={bulkEnable} className="h-7 text-xs">
                      <Shield className="h-3 w-3 mr-1" />Enable
                    </Button>
                    <Button variant="outline" size="sm" onClick={bulkDisable} className="h-7 text-xs">
                      <ShieldOff className="h-3 w-3 mr-1" />Disable
                    </Button>
                    <Button variant="outline" size="sm" onClick={bulkUnlock} className="h-7 text-xs">
                      <Unlock className="h-3 w-3 mr-1" />Unlock
                    </Button>
                    <Button variant="destructive" size="sm" onClick={bulkDelete} className="h-7 text-xs">
                      <Trash2 className="h-3 w-3 mr-1" />Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
            
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[140px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? [...Array(3)].map((_, i) => <TableRow key={i}><TableCell colSpan={4}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>)
                  : filteredUsers.map((u) => (
                    <TableRow key={u._id} className={`cursor-pointer ${selectedUser === u._id ? 'bg-accent/50' : 'hover:bg-accent/30'}`} data-testid={`user-row-${u._id}`}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedUserIds.includes(u._id)}
                          onCheckedChange={() => toggleSelectUser(u._id)}
                        />
                      </TableCell>
                      <TableCell onClick={() => setSelectedUser(u._id)}>
                        <p className="text-sm font-medium">{u.username}</p>
                        <p className="text-xs text-muted-foreground">{u.name} &middot; {u.transaction_count} txns</p>
                      </TableCell>
                      <TableCell onClick={() => setSelectedUser(u._id)}>
                        <Badge variant={u.is_active ? 'default' : 'destructive'} className={`text-xs ${u.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-0' : ''}`}>{u.role}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => impersonateUser(u._id, u.username)} title="View as"><Eye className="h-3 w-3" /></Button>
                          {u.role === 'admin' ? (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-orange-600" onClick={() => demoteUser(u._id, u.username)} title="Demote to User"><ArrowDown className="h-3 w-3" /></Button>
                          ) : (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-purple-600" onClick={() => promoteUser(u._id, u.username)} title="Promote to Admin"><ArrowUp className="h-3 w-3" /></Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleUser(u._id)} title={u.is_active ? 'Disable' : 'Enable'}>{u.is_active ? <ShieldOff className="h-3 w-3" /> : <Shield className="h-3 w-3" />}</Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => resetPassword(u._id, u.username)} title="Reset PW" data-testid={`reset-pw-${u._id}`}><KeyRound className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => unlockUser(u._id, u.username)} title="Clear lockouts" data-testid={`unlock-${u._id}`}><Unlock className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteUser(u._id, u.username)} title="Delete"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* User detail panel - right */}
        <div className="lg:col-span-3">
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                {selectedUser ? `${users.find(u => u._id === selectedUser)?.username || 'User'} — Overview` : 'Select a user to view'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedUser ? <UserDashboardPanel userId={selectedUser} /> : (
                <div className="py-12 text-center text-muted-foreground text-sm">Click on a user from the list to view their dashboard, analytics, net worth, and investments</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function CreateUserForm({ onSave }) {
  const [form, setForm] = useState({ username: '', name: '', password: '', role: 'user' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Min 8 chars'); return; }
    setSaving(true);
    try { await api.post('/api/admin/users', form); toast.success('Created'); onSave(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="create-username-input" value={form.username} onChange={(e) => set('username', e.target.value)} required /></div>
      <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid="create-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
      <div className="space-y-1"><Label className="text-xs">Password</Label><Input type="password" data-testid="create-password-input" value={form.password} onChange={(e) => set('password', e.target.value)} required /></div>
      <div className="space-y-1"><Label className="text-xs">Role</Label><Select value={form.role} onValueChange={(v) => set('role', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
      <Button type="submit" className="w-full" disabled={saving} data-testid="create-user-submit">{saving ? 'Creating...' : 'Create User'}</Button>
    </form>
  );
}
